import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_KEPSEK } from "@/lib/nav";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { EKSKUL_LIST, PROGSUS_LIST } from "@/lib/constants";

const F_EMPTY = { siswa: "", guru: "" };

export default function EPPendampinganKepsek() {
  const [jenis, setJenis] = useState(EKSKUL_LIST[0]);
  const [siswa, setSiswa] = useState([]);
  const [guru, setGuru] = useState([]);
  const [assignments, setAssignments] = useState({}); // siswa_id -> guru_id ("" = "-")
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);

  const loadInit = useCallback(async () => {
    try {
      const [{ data: g }, { data: s }] = await Promise.all([
        api.get("/guru", { params: { status: "aktif" } }),
        api.get("/siswa", { params: { status: "aktif" } }),
      ]);
      setGuru(g); setSiswa(s);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { loadInit(); }, [loadInit]);

  const loadAssignments = useCallback(async () => {
    try {
      const { data } = await api.get("/ekskul-progsus/pendampingan", { params: { jenis } });
      const init = {};
      siswa.forEach((s) => { init[s.id] = ""; });
      data.forEach((a) => { init[a.siswa_id] = a.guru_id || ""; });
      setAssignments(init);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [jenis, siswa]);
  useEffect(() => { if (siswa.length) loadAssignments(); }, [loadAssignments, siswa.length]);

  const guruMap = useMemo(() => Object.fromEntries(guru.map((g) => [g.id, g.nama])), [guru]);

  const setField = (siswa_id, value) => {
    setAssignments((prev) => ({ ...prev, [siswa_id]: value }));
  };

  const visibleSiswa = siswa.filter((s) => {
    if (applied.siswa && !s.nama.toLowerCase().includes(applied.siswa.toLowerCase())) return false;
    if (applied.guru) {
      const namaGuru = (guruMap[assignments[s.id]] || "").toLowerCase();
      if (!namaGuru.includes(applied.guru.toLowerCase())) return false;
    }
    return true;
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const items = siswa.map((s) => ({ siswa_id: s.id, guru_id: assignments[s.id] || "" }));
      const { data } = await api.post("/ekskul-progsus/pendampingan/bulk", { jenis, items });
      toast.success(`Tersimpan: ${data.saved}${data.removed ? `, dihapus: ${data.removed}` : ""}`);
      loadAssignments();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout nav={NAV_KEPSEK}>
      <PageTitle
        title="Ekskul & Progsus — Pendampingan"
        action={
          <button onClick={handleSave} disabled={saving} className="pp-btn-primary" data-testid="ep-pend-save-top">
            <Save className="w-4 h-4" /> {saving ? "Menyimpan…" : "Simpan"}
          </button>
        }
      />

      <section className="pp-card p-5 mb-5">
        <label className="pp-label">Pilih Ekskul / Progsus</label>
        <select
          className="pp-input max-w-xl"
          value={jenis}
          onChange={(e) => setJenis(e.target.value)}
          data-testid="ep-pend-jenis"
        >
          <optgroup label="Ekstrakurikuler">
            {EKSKUL_LIST.map((k) => <option key={k} value={k}>{k}</option>)}
          </optgroup>
          <optgroup label="Program Khusus">
            {PROGSUS_LIST.map((k) => <option key={k} value={k}>{k}</option>)}
          </optgroup>
        </select>
      </section>

      <section className="pp-card p-5 mb-6">
        <div className="grid sm:grid-cols-3 gap-3">
          <input
            className="pp-input" placeholder="Cari nama siswa"
            value={filters.siswa} onChange={(e) => setFilters({ ...filters, siswa: e.target.value })}
            data-testid="ep-pend-filter-siswa"
          />
          <input
            className="pp-input" placeholder="Cari guru pendamping"
            value={filters.guru} onChange={(e) => setFilters({ ...filters, guru: e.target.value })}
            data-testid="ep-pend-filter-guru"
          />
          <div className="flex gap-2">
            <button onClick={() => setApplied({ ...filters })} className="pp-btn-primary flex-1" data-testid="ep-pend-apply">Terapkan</button>
            <button onClick={() => { setFilters(F_EMPTY); setApplied(F_EMPTY); }} className="pp-btn-secondary flex-1">Reset</button>
          </div>
        </div>
      </section>

      <section className="pp-card pp-freeze overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ep-pend-table">
            <thead>
              <tr className="pp-table-header">
                <th className="text-left px-4 py-3">Nama Siswa</th>
                <th className="text-left px-4 py-3">NISN</th>
                <th className="text-left px-4 py-3 min-w-[280px]">Guru Pendamping</th>
              </tr>
            </thead>
            <tbody>
              {visibleSiswa.length === 0 && (
                <tr><td colSpan={3} className="text-center py-10 text-slate-400 italic">Tidak ada siswa aktif.</td></tr>
              )}
              {visibleSiswa.map((s) => (
                <tr key={s.id} className="pp-row" data-testid={`ep-pend-row-${s.id}`}>
                  <td className="px-4 py-2 font-medium text-slate-900">{s.nama}</td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-500">{s.nis}</td>
                  <td className="px-4 py-2">
                    <select
                      className="pp-input"
                      value={assignments[s.id] || ""}
                      onChange={(e) => setField(s.id, e.target.value)}
                      data-testid={`ep-pend-guru-${s.id}`}
                    >
                      <option value="">-</option>
                      {guru.map((g) => <option key={g.id} value={g.id}>{g.nama}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end p-4 border-t border-slate-100">
          <button onClick={handleSave} disabled={saving} className="pp-btn-primary" data-testid="ep-pend-save-bottom">
            <Save className="w-4 h-4" /> {saving ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </section>
    </Layout>
  );
}
